// Phase 1 prototype: client-side CSV and NACHA export generators for payment runs.
// These produce realistic, downloadable files so admins can validate format and flow.
// Real production exports will be generated server-side with encrypted bank data.

const ODFI_ROUTING = "021000322";      // demo originating bank routing (JPMorgan Chase NYC)
const COMPANY_NAME = "GCBC PAYOUTS";
const COMPANY_ID   = "1234567890";     // 10-digit IRS EIN placeholder

function triggerDownload(filename, content, mime) {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Revoke after a tick so some browsers (Safari) complete the download.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function csvEscape(v) {
  if (v == null) return "";
  const s = String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function pad(value, len, side = "right", filler = " ") {
  const s = String(value ?? "");
  if (s.length >= len) return s.slice(0, len);
  return side === "left" ? s.padStart(len, filler) : s.padEnd(len, filler);
}

function nachaDate(d) {
  const dt = d instanceof Date ? d : new Date(d || Date.now());
  return dt.getUTCFullYear().toString().slice(2)
       + String(dt.getUTCMonth() + 1).padStart(2, "0")
       + String(dt.getUTCDate()).padStart(2, "0");
}

function nachaTime(d = new Date()) {
  return String(d.getUTCHours()).padStart(2, "0") + String(d.getUTCMinutes()).padStart(2, "0");
}

// Compute simple ABA checksum-style sum for NACHA file/batch control hash.
// NACHA hash = sum of first 8 digits of each entry's RDFI routing, mod 10^10.
function routingHash(routings) {
  const sum = routings.reduce((acc, r) => acc + Number(String(r).slice(0, 8)) || 0, 0);
  return String(sum).slice(-10).padStart(10, "0");
}

// ─── CSV ──────────────────────────────────────────────────────────────────
export function downloadPaymentRunCsv(run, items, lookups) {
  const { getMemberById, getDealById, getBrandById } = lookups;
  const headers = [
    "PaymentId", "MemberId", "MemberName", "DealId", "Brand",
    "RoutingNumber", "AccountNumberLast4", "Amount", "Status", "RefNumber", "RunId", "CutoffDate",
  ];
  const rows = items.map((p) => {
    const m = getMemberById(p.memberId);
    const deal = getDealById(p.dealId);
    const brand = deal ? getBrandById(deal.brandId) : null;
    return [
      p.id,
      m?.id || "",
      m ? `${m.firstName} ${m.lastName}` : "",
      deal?.id || "",
      brand?.name || "",
      ODFI_ROUTING,
      String(p.bankLast4 || "").padStart(4, "0"),
      (p.amount || 0).toFixed(2),
      p.status,
      p.refNumber || "",
      run.id,
      run.cutoffDate || "",
    ];
  });

  const totals = ["", "", "", "", "", "", "TOTAL", items.reduce((a, x) => a + (x.amount || 0), 0).toFixed(2), "", "", "", ""];
  const csv = [headers, ...rows, totals].map((r) => r.map(csvEscape).join(",")).join("\n");

  const fname = `${run.id}_${nachaDate(run.cutoffDate || new Date())}.csv`;
  triggerDownload(fname, csv, "text/csv;charset=utf-8");
  return { filename: fname, count: items.length };
}

// ─── NACHA (simplified PPD credit batch) ──────────────────────────────────
// File structure: File Header (1) · Batch Header (5) · Entry Details (6) · Batch Control (8) · File Control (9) · Padding (9s)
// Each record is exactly 94 characters. This is a representative PPD credit file, not certified.
export function downloadPaymentRunNacha(run, items, lookups) {
  const { getMemberById } = lookups;
  const now = new Date();
  const effDate = nachaDate(run.cutoffDate || now);
  const fileDate = nachaDate(now);
  const fileTime = nachaTime(now);

  // 1 — File Header
  const fileHeader =
    "1" +                                  // record type
    "01" +                                 // priority code
    " " + pad(ODFI_ROUTING, 9, "left") +   // immediate destination (with leading blank)
    " " + pad(COMPANY_ID, 9, "left") +     // immediate origin
    fileDate + fileTime +                  // file creation date/time
    "A" +                                  // file id modifier
    "094" +                                // record size
    "10" +                                 // blocking factor
    "1" +                                  // format code
    pad("DEMO BANK", 23) +                 // immediate destination name
    pad(COMPANY_NAME, 23) +                // immediate origin name
    pad(run.id.toUpperCase(), 8);          // reference code

  // 5 — Batch Header
  const batchHeader =
    "5" +
    "220" +                                // service class code (220 = credits only)
    pad(COMPANY_NAME, 16) +
    pad("", 20) +                          // discretionary data
    pad(COMPANY_ID, 10) +
    "PPD" +                                // standard entry class
    pad("GIFTCARD", 10) +                  // company entry description
    pad(effDate, 6) +                      // company descriptive date
    effDate +                              // effective entry date
    pad("", 3) +                           // settlement date (bank-assigned)
    "1" +                                  // originator status
    pad(ODFI_ROUTING.slice(0, 8), 8) +     // originating DFI id
    "0000001";                             // batch number

  // 6 — Entry Detail records
  let entryHash = 0;
  let totalCredit = 0;
  let entryCount = 0;
  const entries = items.map((p, idx) => {
    const m = getMemberById(p.memberId);
    const name = m ? `${m.firstName} ${m.lastName}` : "UNKNOWN MEMBER";
    const amountCents = Math.round((p.amount || 0) * 100);
    totalCredit += amountCents;
    entryHash += Number(ODFI_ROUTING.slice(0, 8)) || 0;
    entryCount += 1;
    const traceNumber = pad(ODFI_ROUTING.slice(0, 8), 8) + String(idx + 1).padStart(7, "0");
    const acct = String(p.bankLast4 || "0000").padStart(4, "0");
    return (
      "6" +
      "22" +                                  // transaction code 22 = checking credit
      pad(ODFI_ROUTING.slice(0, 8), 8) +      // receiving DFI id
      String(ODFI_ROUTING.slice(8, 9)) +      // check digit
      pad(acct, 17) +                         // DFI account number
      pad(String(amountCents), 10, "left", "0") +
      pad(m?.id || p.memberId, 15) +          // individual id number
      pad(name.toUpperCase(), 22) +
      "  " +                                  // discretionary data
      "0" +                                   // addenda record indicator
      traceNumber
    );
  });

  // 8 — Batch Control
  const hash = String(entryHash).slice(-10).padStart(10, "0");
  const batchControl =
    "8" +
    "220" +
    pad(String(entryCount), 6, "left", "0") +
    pad(hash, 10, "left", "0") +
    pad("0", 12, "left", "0") +              // total debit (none in credit batch)
    pad(String(totalCredit), 12, "left", "0") +
    pad(COMPANY_ID, 10) +
    pad("", 19) +                            // message authentication code
    pad("", 6) +                             // reserved
    pad(ODFI_ROUTING.slice(0, 8), 8) +
    "0000001";

  // 9 — File Control
  const totalRecords = 2 + entryCount + 2;   // header + batch header + entries + batch control + file control
  const blockCount = Math.ceil(totalRecords / 10);
  const fileControl =
    "9" +
    "000001" +                                // batch count
    pad(String(blockCount), 6, "left", "0") +
    pad(String(entryCount), 8, "left", "0") +
    pad(hash, 10, "left", "0") +
    pad("0", 12, "left", "0") +
    pad(String(totalCredit), 12, "left", "0") +
    pad("", 39);

  const lines = [fileHeader, batchHeader, ...entries, batchControl, fileControl];
  // Pad final block with 9-filled records to reach a multiple of 10
  const filler = "9".repeat(94);
  while (lines.length % 10 !== 0) lines.push(filler);

  const content = lines.join("\n") + "\n";
  const fname = `${run.id}_${effDate}.ach`;
  triggerDownload(fname, content, "text/plain;charset=us-ascii");
  return { filename: fname, count: items.length };
}
